import { useEffect, useState } from 'react'
import type { Partner } from '@shared/types'
import { costEvidenceUrl, fetchCostVariations, type CostVariationLogRow } from '../lib/api.js'

/**
 * Cost log (decision 97) — every cost variation across the whole portal, for
 * Wave9 internal users. Each entry: who, when, why, which lines old→new, and
 * the supporting document. Entries are append-only evidence — nothing here
 * edits or deletes; the record IS the point.
 *
 * ⚠️ Staff-only by nav gate AND by the API (`isStaffRequest`) — unit cost is
 * the most sensitive figure in the system (decision 53).
 */

export function CostLog({ partner, onOpen }: {
  partner: Partner | null
  onOpen: (quoteId: string) => void
}) {
  const [rows, setRows] = useState<CostVariationLogRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    fetchCostVariations().then((r) => setRows(r.variations)).catch((e: Error) => setError(e.message))
  }, [])

  if (error && !rows) return <p className="pill pill--stop">{error}</p>
  if (!rows) return <p className="muted">Loading…</p>

  const q = query.trim().toLowerCase()
  const shown = q
    ? rows.filter((v) =>
        `${v.quoteNumber} ${v.quoteTitle} ${v.createdBy}`.toLowerCase().includes(q))
    : rows

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Cost variation log</h1>
          <p>
            Supporting evidence for every cost override: who recorded it, when, why, the
            lines it covers old → new, and the supplier document where one was attached. Wave9 internal
            users only — cost never reaches partner sessions.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <div className="toolbar">
            <input className="input" style={{ width: 300 }} placeholder="Search — quote number, title or owner…"
              value={query} onChange={(e) => setQuery(e.target.value)} />
            <h2 style={{ margin: 0 }}>{shown.length} of {rows.length} entr{rows.length === 1 ? 'y' : 'ies'} — newest first</h2>
          </div>
          <span className="muted small">Logged from the builder when a line's cost is overridden on a draft.</span>
        </div>
        <div className="card__body">
          {shown.length === 0 && (
            <p className="muted small">
              {rows.length === 0
                ? 'Nothing yet. Overriding a line\'s cost in the builder (internal quotes, drafts) flags it until a variation entry is logged here with a note or supplier quote.'
                : 'Nothing matches that search.'}
            </p>
          )}
          {shown.map((v) => (
            <div key={v.id} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <strong>{v.quoteNumber}</strong>{v.quoteTitle ? ` — ${v.quoteTitle}` : ''}{' '}
                  <span className="pill">{v.quoteStatus}</span>{' '}
                  {/* Decision 159: the varied cost carried into the successor — say where it lives now. */}
                  {v.currentQuoteNumber && (
                    <span className="pill pill--info" title="This quote was superseded — the varied costs carried into the successor, where this entry still applies.">
                      → now {v.currentQuoteNumber} ({v.currentQuoteStatus})
                    </span>
                  )}{' '}
                  <span className="pill pill--info">{v.partnerName}</span>
                  <div className="small muted">
                    {new Date(v.createdAt).toLocaleString('en-GB')} · {v.createdBy}
                    {v.evidenceFilename && <> · <a href={costEvidenceUrl(v.id)} target="_blank" rel="noreferrer">📎 {v.evidenceFilename}</a></>}
                  </div>
                  <div className="small" style={{ marginTop: 3 }}>{v.description}</div>
                  <div className="tiny muted" style={{ marginTop: 2 }}>
                    {v.lines.map((l) => `${l.lineName}: £${l.oldCost.toFixed(2)} → £${l.newCost.toFixed(2)}`).join(' · ')}
                  </div>
                </div>
                <button className="btn btn--sm" disabled={!partner}
                  title={partner ? 'Opens the quote in the builder — switch “Acting as” first if it belongs to a different partner.' : 'Choose a partner under "Acting as" first.'}
                  onClick={() => onOpen(v.currentQuoteId ?? v.quoteId)}>
                  {v.currentQuoteId ? 'Open current quote →' : 'Open quote →'}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
